import type { components } from './generated/schema'

type AgentStatus = components['schemas']['AgentStatus']
type EventLog = components['schemas']['EventLog']
type UserAgent = components['schemas']['UserAgent']

export type UnifiedAvailability = 'Online' | 'Away'

export interface UnifiedCustomStatus {
  id: string
  name: string
  description: string
  availability: UnifiedAvailability
  pbx_id?: number
}

export interface UnifiedUserAgent {
  id: string
  user_id?: number
  user_uuid?: string | null
  user_first_name?: string | null
  user_last_name?: string | null
  extension: string
  agent_status?: UnifiedCustomStatus
  updated_at: string
  created_at: string
  logged_in: boolean
}

export interface UnifiedEventLog {
  id: string
  event_type?: 'agent_status' | 'queue' | 'user'
  agent_status: string
  agent_availability?: string
  call_center_user_agent_id?: string
  extension_uuid: string
  user_id?: number
  user_uuid?: string | null
  initiated_by_user_id?: number
  initiated_by_user_uuid?: string | null
  initiated_by_name?: string | null
  created_at: string
  logged_in: boolean
}

export function toOpaqueId(id: string | number | null | undefined): string {
  return id == null ? '' : String(id)
}

export function mapAvailability(
  value: string | null | undefined,
): UnifiedAvailability {
  return value === 'Online' ? 'Online' : 'Away'
}

// A user with no assigned agent status can arrive in two shapes:
//   - the field is omitted or null; or
//   - a zero-value sentinel (nil UUID) is sent instead (CUS-911).
// Keep those as "no status". A real UUID with missing availability is a
// backend hydration bug (CUS-964): do NOT map that to "no status", or
// useCallQueue.reconcileFromServer will treat Logged Out + !agent_status as
// OutOfQueue and kick the agent from the queue UI. mapAvailability still
// coerces a null availability to 'Away' until backends hydrate.
const NIL_UUID = '00000000-0000-0000-0000-000000000000'

export function isAssignedAgentStatus<
  T extends { id?: string | number | null; availability?: string | null },
>(raw: T | null | undefined): raw is T {
  if (!raw) return false
  const id = toOpaqueId(raw.id)
  return id !== '' && id !== NIL_UUID
}

export function mapCustomStatus(raw: {
  id: string | number
  name: string
  description?: string | null
  availability?: string | null
  pbx_id?: number | null
}): UnifiedCustomStatus {
  return {
    id: toOpaqueId(raw.id),
    name: raw.name,
    description: raw.description ?? '',
    availability: mapAvailability(raw.availability),
    ...(raw.pbx_id != null ? { pbx_id: raw.pbx_id } : {}),
  }
}

export function mapGatewayAgentStatus(
  status: AgentStatus,
): UnifiedCustomStatus {
  return mapCustomStatus(status)
}

export function mapUserAgent(raw: {
  id: string | number
  user_id?: number
  user_uuid?: string | null
  user_first_name?: string | null
  user_last_name?: string | null
  extension?: string
  extension_uuid?: string
  agent_status?: {
    id: string | number
    name: string
    description?: string | null
    availability?: string | null
    pbx_id?: number | null
  } | null
  updated_at: string
  created_at: string
  logged_in?: boolean
}): UnifiedUserAgent {
  return {
    id: toOpaqueId(raw.id),
    user_id: raw.user_id,
    user_uuid: raw.user_uuid,
    user_first_name: raw.user_first_name,
    user_last_name: raw.user_last_name,
    extension: raw.extension ?? raw.extension_uuid ?? '',
    agent_status: isAssignedAgentStatus(raw.agent_status)
      ? mapCustomStatus(raw.agent_status)
      : undefined,
    updated_at: raw.updated_at,
    created_at: raw.created_at,
    logged_in: !!raw.logged_in,
  }
}

export function mapGatewayUserAgent(userAgent: UserAgent): UnifiedUserAgent {
  return mapUserAgent(userAgent)
}

export function mapEventLog(raw: {
  id: string | number
  event_type?: string
  agent_status?: string | null
  agent_availability?: string | null
  call_center_user_agent_id?: string | number | null
  extension_uuid?: string
  user_id?: number
  user_uuid?: string | null
  initiated_by_user_id?: number
  initiated_by_user_uuid?: string | null
  initiated_by_name?: string | null
  created_at: string
  logged_in?: boolean
}): UnifiedEventLog {
  const eventType =
    raw.event_type === 'agent_status' ||
    raw.event_type === 'queue' ||
    raw.event_type === 'user'
      ? raw.event_type
      : undefined
  return {
    id: toOpaqueId(raw.id),
    event_type: eventType,
    agent_status: raw.agent_status ?? '',
    agent_availability: raw.agent_availability ?? undefined,
    call_center_user_agent_id:
      raw.call_center_user_agent_id == null
        ? undefined
        : toOpaqueId(raw.call_center_user_agent_id),
    extension_uuid: raw.extension_uuid ?? '',
    user_id: raw.user_id,
    user_uuid: raw.user_uuid,
    initiated_by_user_id: raw.initiated_by_user_id,
    initiated_by_user_uuid: raw.initiated_by_user_uuid,
    initiated_by_name: raw.initiated_by_name,
    created_at: raw.created_at,
    logged_in: !!raw.logged_in,
  }
}

export function mapGatewayEventLog(eventLog: EventLog): UnifiedEventLog {
  return mapEventLog(eventLog)
}

export function toRfc3339DayStart(date: string): string {
  return `${date}T00:00:00.000Z`
}

export function toRfc3339DayEnd(date: string): string {
  return `${date}T23:59:59.999Z`
}
